import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PinDropIcon from '@mui/icons-material/PinDrop';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import {
  Box,
  CircularProgress,
  InputBase,
  Paper,
  Tooltip as MuiTooltip,
  Typography,
  type TooltipProps,
} from '@mui/material';
import { useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  useNewVsLostAccounts,
  type AccountBrief,
  type NewVsLostAccountsResponse,
} from '../api/depletions';
import { useClickOutside } from '../hooks/useClickOutside';
import { fmt6PkNumber, toSixPacks } from '../lib/volume';
import { colors } from '../theme';
import {
  accountColumns,
  excelSheet,
  isoDate,
  num as toNum,
  premisesLabel,
  type ExcelColumn,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';
import { PremisesBadge } from './PremisesBadge';
import { useSectionExport } from '../hooks/useSectionExport';
import { SectionExplainer } from './SectionExplainer';

const num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

// ----------------------------------------------------------------
// Premium light tooltip — same shape used across the depletions
// sections so hover affordances feel consistent.
// ----------------------------------------------------------------

interface RichTooltipProps {
  title: TooltipProps['title'];
  children: ReactElement;
  placement?: TooltipProps['placement'];
  maxWidth?: number;
}

function Tooltip({ title, children, placement = 'top', maxWidth = 280 }: RichTooltipProps) {
  return (
    <MuiTooltip
      title={title}
      placement={placement}
      arrow
      enterDelay={120}
      enterNextDelay={50}
      leaveDelay={40}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.05)',
            fontSize: 11.5,
            lineHeight: 1.45,
            maxWidth,
            padding: '8px 10px',
            fontFeatureSettings: '"tnum","lnum"',
          },
        },
        arrow: {
          sx: {
            color: '#fff',
            '&::before': {
              border: `1px solid ${colors.border}`,
              backgroundColor: '#fff',
            },
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
}

// ----------------------------------------------------------------
// Structured tooltip block — used for portfolio tiles so each
// hover surface reads as docs (title · definition rows · italic
// "how to read it" footer) rather than a paragraph of prose.
// ----------------------------------------------------------------

interface TooltipBlockProps {
  title?: string;
  items?: Array<{ label: string; description: string }>;
  bullets?: string[];
  tip?: string;
  maxWidth?: number;
}

function TooltipBlock({ title, items, bullets, tip, maxWidth = 260 }: TooltipBlockProps) {
  return (
    <Box sx={{ p: 0.25, maxWidth }}>
      {title && (
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.textPrimary, mb: 0.75 }}>
          {title}
        </Typography>
      )}
      {items && items.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.6,
            mb: bullets || tip ? 0.75 : 0,
          }}
        >
          {items.map((it) => (
            <Box key={it.label}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1.3,
                }}
              >
                {it.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}>
                {it.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {bullets && bullets.length > 0 && (
        <Box
          component="ul"
          sx={{
            pl: 0,
            m: 0,
            mb: tip ? 0.75 : 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.3,
          }}
        >
          {bullets.map((b, i) => (
            <Box
              component="li"
              key={i}
              sx={{
                fontSize: 11,
                color: colors.textSecondary,
                lineHeight: 1.4,
                position: 'relative',
                pl: 1.25,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 2,
                  top: 7,
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: colors.textMuted,
                },
              }}
            >
              {b}
            </Box>
          ))}
        </Box>
      )}
      {tip && (
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textMuted,
            fontStyle: 'italic',
            lineHeight: 1.4,
            borderTop: `1px solid ${colors.borderLight}`,
            pt: 0.6,
            mt: items || bullets ? 0.5 : 0,
          }}
        >
          {tip}
        </Typography>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Hero / portfolio tiles
// ----------------------------------------------------------------

interface PortfolioTileProps {
  eyebrow: string;
  primary: ReactNode;
  primaryColor?: string;
  secondary?: ReactNode;
  tooltip?: ReactNode;
}

function PortfolioTile({ eyebrow, primary, primaryColor, secondary, tooltip }: PortfolioTileProps) {
  const body = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 1.5,
        borderRight: `1px solid ${colors.borderLight}`,
        '&:last-of-type': { borderRight: 'none' },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        cursor: tooltip ? 'help' : 'default',
        transition: 'background-color 0.12s',
        '&:hover': tooltip ? { bgcolor: '#fff' } : undefined,
      }}
    >
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: colors.textMuted,
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </Typography>
      <Box
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: primaryColor ?? colors.chartPrimary,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFeatureSettings: '"tnum","lnum"',
        }}
      >
        {primary}
      </Box>
      {secondary !== undefined && (
        <Box sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.3 }}>{secondary}</Box>
      )}
    </Box>
  );
  if (!tooltip) return body;
  return <Tooltip title={tooltip}>{body}</Tooltip>;
}

// ----------------------------------------------------------------
// Filter pill (state multi-select)
// ----------------------------------------------------------------

interface StateMultiSelectProps {
  available: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  open: boolean;
  onToggle: () => void;
}

function StateMultiSelect({
  available,
  selected,
  onChange,
  open,
  onToggle,
}: StateMultiSelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, onToggle, open);
  const valueLabel =
    selected.size === 0
      ? 'All states'
      : selected.size === 1
        ? [...selected][0]
        : `${selected.size} states`;
  const toggle = (code: string) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(next);
  };
  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${open || selected.size > 0 ? colors.chartPrimary : colors.border}`,
          bgcolor: open || selected.size > 0 ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 12,
          fontWeight: 500,
          color: colors.textPrimary,
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 11 }}>
          State:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {valueLabel}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 10,
            minWidth: 280,
            maxWidth: 360,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
            p: 1.25,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 0.75,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.textMuted }}>
              {available.length} states
            </Typography>
            {selected.size > 0 && (
              <Box
                component="button"
                onClick={() => onChange(new Set())}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.chartPrimary,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear all
              </Box>
            )}
          </Box>
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 240, overflow: 'auto' }}
          >
            {available.map((code) => {
              const isSel = selected.has(code);
              return (
                <Box
                  key={code}
                  component="button"
                  onClick={() => toggle(code)}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    px: 1,
                    py: 0.4,
                    borderRadius: 0.75,
                    border: `1px solid ${isSel ? colors.chartPrimary : colors.border}`,
                    bgcolor: isSel ? colors.chartPrimary : '#fff',
                    color: isSel ? '#fff' : colors.textPrimary,
                    fontSize: 11.5,
                    fontWeight: isSel ? 700 : 500,
                    fontFeatureSettings: '"tnum"',
                    '&:hover': {
                      borderColor: colors.chartPrimary,
                      bgcolor: isSel ? colors.chartPrimary : `${colors.chartPrimary}10`,
                    },
                  }}
                >
                  {code}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

function SearchInput({ value, onChange, placeholder }: SearchInputProps) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.4,
        borderRadius: 1,
        border: `1px solid ${value ? colors.chartPrimary : colors.border}`,
        bgcolor: '#fff',
        minWidth: 240,
        transition: 'border-color 0.12s',
        '&:hover': { borderColor: colors.chartPrimary },
        '&:focus-within': {
          borderColor: colors.chartPrimary,
          boxShadow: `0 0 0 3px ${colors.chartPrimary}1a`,
        },
      }}
    >
      <SearchIcon sx={{ fontSize: 16, color: colors.textMuted }} />
      <InputBase
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          flex: 1,
          fontSize: 12,
          color: colors.textPrimary,
          '& input::placeholder': { color: colors.textMuted, opacity: 1, fontSize: 11.5 },
        }}
      />
      {value && (
        <Box
          component="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            color: colors.textMuted,
            '&:hover': { bgcolor: colors.surfaceHover, color: colors.textPrimary },
          }}
        >
          <ClearIcon sx={{ fontSize: 13 }} />
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Inline insight row — compact geographic / channel callouts that
// belong with this analytical section. Per-account "what to call"
// lists live in the Follow-Up Tracker so the rep has one hub.
// ----------------------------------------------------------------

interface InsightChip {
  key: string;
  Icon: typeof PersonAddIcon;
  color: string;
  label: string;
  primary: string;
  detail: string;
}

interface InsightRowProps {
  eyebrow: string;
  chips: InsightChip[];
  followUpHint?: string;
}

function InsightRow({ eyebrow, chips, followUpHint }: InsightRowProps) {
  if (chips.length === 0 && !followUpHint) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        px: 1.5,
        py: 1,
        mb: 2.5,
        borderRadius: 1.25,
        bgcolor: colors.surfaceSubtle,
        border: `1px solid ${colors.borderLight}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          mr: 0.5,
        }}
      >
        {eyebrow}
      </Typography>
      {chips.map((c, i) => (
        <Box
          key={c.key}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            pr: 1,
            borderRight: i === chips.length - 1 ? 'none' : `1px solid ${colors.borderLight}`,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: 0.75,
              bgcolor: `${c.color}14`,
              color: c.color,
              flexShrink: 0,
            }}
          >
            <c.Icon sx={{ fontSize: 13 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                {c.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: c.color,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {c.primary}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.textSecondary,
                lineHeight: 1.3,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {c.detail}
            </Typography>
          </Box>
        </Box>
      ))}
      <Box sx={{ flex: 1 }} />
      {followUpHint && (
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textMuted,
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
          }}
        >
          {followUpHint}
        </Typography>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Account row + list panel
// ----------------------------------------------------------------

interface AccountRowProps {
  acc: AccountBrief;
  rank: number;
  color: string;
  maxVolume: number;
  isLost: boolean;
}

function AccountRow({ acc, rank, color, maxVolume, isLost }: AccountRowProps) {
  const volume = Number(acc.cases_9l);
  const barPct = maxVolume > 0 ? (volume / maxVolume) * 100 : 0;
  const cityState = [acc.city, acc.state_code].filter(Boolean).join(', ') || '—';
  return (
    <Tooltip
      placement="left"
      title={
        <Box sx={{ p: 0.25, maxWidth: 240 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.4 }}>{acc.name}</Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto',
              gap: '4px 12px',
              fontSize: 11,
              fontFeatureSettings: '"tnum"',
            }}
          >
            <span style={{ opacity: 0.7 }}>Location</span>
            <span style={{ fontWeight: 600 }}>{cityState}</span>
            <span style={{ opacity: 0.7 }}>Distributor</span>
            <span style={{ fontWeight: 600 }}>{acc.distributor_name ?? '—'}</span>
            <span style={{ opacity: 0.7 }}>
              {isLost ? 'Prior-window volume' : 'Recent-window volume'}
            </span>
            <span style={{ fontWeight: 700, color }}>
              {fmt6PkNumber(volume)} 6-pk · {num.format(volume)} 9L
            </span>
          </Box>
          <Typography
            sx={{ fontSize: 10.5, color: colors.textMuted, mt: 0.5, fontStyle: 'italic' }}
          >
            {isLost
              ? 'Was active in the prior window; silent in the recent window.'
              : 'Silent in the prior window; active in the recent window.'}
          </Typography>
        </Box>
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.85,
          borderBottom: `1px solid ${colors.borderLight}`,
          '&:last-of-type': { borderBottom: 'none' },
          cursor: 'help',
          transition: 'background-color 0.12s',
          '&:hover': { bgcolor: `${color}0a` },
        }}
      >
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            color: colors.textMuted,
            width: 22,
            textAlign: 'right',
            flexShrink: 0,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {rank}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {acc.name}
            </Typography>
            <PremisesBadge premises={acc.premises_type} online={acc.is_online_retailer} />
          </Box>
          <Typography
            sx={{ fontSize: 10.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}
          >
            {cityState}
            {acc.distributor_name && ` · ${acc.distributor_name}`}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
            width: 120,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: 5,
              bgcolor: `${color}1a`,
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${barPct}%`,
                height: '100%',
                borderRadius: 3,
                bgcolor: color,
                opacity: 0.65,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color,
              minWidth: 50,
              textAlign: 'right',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {num.format(volume)}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

interface AccountPanelProps {
  title: string;
  caption: string;
  icon: typeof PersonAddIcon;
  color: string;
  accounts: AccountBrief[];
  totalCount: number;
  totalVolume: number;
  isLost: boolean;
}

function AccountPanel({
  title,
  caption,
  icon: Icon,
  color,
  accounts,
  totalCount,
  totalVolume,
  isLost,
}: AccountPanelProps) {
  const maxVolume = accounts.reduce((acc, a) => Math.max(acc, Number(a.cases_9l)), 0) || 1;
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: `1px solid ${color}33`,
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          bgcolor: `${color}08`,
          borderBottom: `1px solid ${color}26`,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            bgcolor: `${color}1a`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: colors.textPrimary }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11, color: colors.textMuted }}>{caption}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              color,
              lineHeight: 1,
              fontFeatureSettings: '"tnum"',
              letterSpacing: '-0.02em',
            }}
          >
            {totalCount}
          </Typography>
          <Typography sx={{ fontSize: 10, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
            {isLost ? '−' : '+'}
            {fmt6PkNumber(totalVolume)} 6-pk · {num.format(totalVolume)} 9L
          </Typography>
        </Box>
      </Box>
      {accounts.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>
            No accounts match the current filters.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
          {accounts.map((acc, i) => (
            <AccountRow
              key={acc.account_id}
              acc={acc}
              rank={i + 1}
              color={color}
              maxVolume={maxVolume}
              isLost={isLost}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Aggregate helpers — used by both the portfolio strip and the
// recommendations panel.
// ----------------------------------------------------------------

function tallyBy<T extends string>(
  rows: AccountBrief[],
  key: (a: AccountBrief) => T | null,
): Map<T, { count: number; volume: number }> {
  const map = new Map<T, { count: number; volume: number }>();
  for (const r of rows) {
    const k = key(r);
    if (k === null) continue;
    const cur = map.get(k) ?? { count: 0, volume: 0 };
    cur.count += 1;
    cur.volume += Number(r.cases_9l);
    map.set(k, cur);
  }
  return map;
}

function topEntry<T extends string>(
  m: Map<T, { count: number; volume: number }>,
): { key: T; count: number; volume: number } | null {
  let best: { key: T; count: number; volume: number } | null = null;
  for (const [k, v] of m) {
    if (!best || v.count > best.count || (v.count === best.count && v.volume > best.volume)) {
      best = { key: k, ...v };
    }
  }
  return best;
}

// ----------------------------------------------------------------
// Main component
// ----------------------------------------------------------------

interface SummaryRow {
  label: string;
  value: string | number | Date | null;
}

// `window` labels the volume: recent window for new accounts, prior for lost.
function accountSheetColumns(window: string): ExcelColumn<AccountBrief>[] {
  return [
    ...accountColumns<AccountBrief>(),
    { header: 'Distributor', value: (r) => r.distributor_name, width: 24 },
    { header: 'Premises', value: (r) => premisesLabel(r.premises_type, r.is_online_retailer) },
    {
      header: `9L cases (${window})`,
      value: (r) => toNum(r.cases_9l),
      format: 'decimal',
    },
    {
      header: `6-packs (${window})`,
      value: (r) => Math.round(toSixPacks(r.cases_9l)),
      format: 'int',
    },
  ];
}

function buildNewVsLostExport(
  data: NewVsLostAccountsResponse,
  visibleNew: AccountBrief[],
  visibleLost: AccountBrief[],
  filters: ExportFilter[],
): ExcelWorkbookSpec {
  const summary: SummaryRow[] = [
    { label: 'Window (months)', value: data.window_months },
    { label: 'Reference date', value: isoDate(data.reference_date) },
    { label: 'New accounts', value: data.new_count },
    { label: 'Lost accounts', value: data.lost_count },
    { label: 'New 9L cases', value: toNum(data.new_total_9l) },
    { label: 'Lost 9L cases', value: toNum(data.lost_total_9l) },
  ];
  const n = data.window_months;
  return {
    title: 'New vs Lost Accounts',
    filters,
    sheets: [
      excelSheet<SummaryRow>({
        name: 'Summary',
        columns: [
          { header: 'Metric', value: (r) => r.label, width: 20 },
          // Dates and numbers mixed in one column — Excel keeps each cell's own type.
          { header: 'Value', value: (r) => r.value, width: 14 },
        ],
        rows: summary,
      }),
      excelSheet({
        name: 'New Accounts',
        columns: accountSheetColumns(`last ${n} mo`),
        rows: visibleNew,
      }),
      excelSheet({
        name: 'Lost Accounts',
        columns: accountSheetColumns(`prior ${n} mo`),
        rows: visibleLost,
      }),
    ],
  };
}

export function NewVsLostAccounts() {
  const { data, isLoading, error } = useNewVsLostAccounts();
  const [search, setSearch] = useState('');
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set());
  const [stateMenuOpen, setStateMenuOpen] = useState(false);

  const newAccounts = useMemo(() => data?.new_accounts ?? [], [data?.new_accounts]);
  const lostAccounts = useMemo(() => data?.lost_accounts ?? [], [data?.lost_accounts]);
  const newCount = data?.new_count ?? 0;
  const lostCount = data?.lost_count ?? 0;
  const newVol = Number(data?.new_total_9l ?? 0);
  const lostVol = Number(data?.lost_total_9l ?? 0);
  const netCount = newCount - lostCount;
  const netVol = newVol - lostVol;
  // Replacement ratio — > 1 means we replaced more than we lost.
  const replacementRatio = lostVol > 0 ? newVol / lostVol : newVol > 0 ? Infinity : 0;

  // Tallies (full sets, NOT filtered) so the portfolio + recommendations
  // describe the underlying state regardless of search/state filter.
  const lostByState = useMemo(() => tallyBy(lostAccounts, (a) => a.state_code), [lostAccounts]);
  const lostByDistributor = useMemo(
    () => tallyBy(lostAccounts, (a) => a.distributor_name),
    [lostAccounts],
  );
  const newByState = useMemo(() => tallyBy(newAccounts, (a) => a.state_code), [newAccounts]);
  const hotspotLostState = useMemo(() => topEntry(lostByState), [lostByState]);
  const hotspotNewState = useMemo(() => topEntry(newByState), [newByState]);
  // Aliases used by the inline insight row for readability.
  const topLostState = hotspotLostState;
  const topChurnDistributor = useMemo(() => topEntry(lostByDistributor), [lostByDistributor]);

  // Available states across both lists for the filter pill.
  const availableStates = useMemo(() => {
    const set = new Set<string>();
    for (const a of newAccounts) if (a.state_code) set.add(a.state_code);
    for (const a of lostAccounts) if (a.state_code) set.add(a.state_code);
    return [...set].sort();
  }, [newAccounts, lostAccounts]);

  // Filter the lists for display (search + state).
  const filterList = (rows: AccountBrief[]) => {
    const q = search.trim().toLowerCase();
    return rows.filter((a) => {
      if (q) {
        const hit =
          a.name.toLowerCase().includes(q) ||
          (a.city ?? '').toLowerCase().includes(q) ||
          (a.state_code ?? '').toLowerCase().includes(q) ||
          (a.distributor_name ?? '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (selectedStates.size > 0) {
        if (!a.state_code || !selectedStates.has(a.state_code)) return false;
      }
      return true;
    });
  };
  const visibleNew = useMemo(
    () => filterList(newAccounts).sort((a, b) => Number(b.cases_9l) - Number(a.cases_9l)),
    // filterList is defined inline and depends only on the closure
    // values (search, selectedStates) — listing them here mirrors the
    // closure so React sees the right dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newAccounts, search, selectedStates],
  );
  const visibleLost = useMemo(
    () => filterList(lostAccounts).sort((a, b) => Number(b.cases_9l) - Number(a.cases_9l)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lostAccounts, search, selectedStates],
  );

  useSectionExport(
    () =>
      data
        ? buildNewVsLostExport(data, visibleNew, visibleLost, [
            {
              label: 'Search (name, city, state or distributor)',
              value: search.trim() ? `"${search.trim()}"` : '',
            },
            {
              label: 'State',
              value: selectedStates.size > 0 ? [...selectedStates].sort().join(', ') : 'All states',
            },
            { kind: 'sort', label: 'Sorted by', value: '9L cases (high → low)' },
            {
              kind: 'info',
              label: 'Comparison window',
              value: `Last ${data.window_months} months vs prior ${data.window_months} months`,
            },
            {
              kind: 'info',
              label: 'Recent window',
              value: `${data.recent_window_start} to ${data.reference_date}`,
            },
            {
              kind: 'info',
              label: 'Prior window',
              value: `${data.prior_window_start} to ${data.prior_window_end}`,
            },
            { kind: 'info', label: 'Data through', value: data.reference_date },
          ])
        : null,
    !!data,
  );

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, border: `1px solid ${colors.error}40`, bgcolor: `${colors.error}05` }}
      >
        <Typography color="error">Failed to load new vs lost accounts: {error.message}</Typography>
      </Paper>
    );
  }
  if (isLoading || !data) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          New vs Lost Accounts
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  const recentLabel = formatWindow(data.recent_window_start, data.reference_date);
  const priorLabel = formatWindow(data.prior_window_start, data.prior_window_end);

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${colors.chartPrimary}14`,
          }}
        >
          <PersonAddIcon sx={{ color: colors.chartPrimary, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            New vs Lost Accounts
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Accounts gained or lost between the recent {data.window_months}-month window (
            {recentLabel}) and the prior window ({priorLabel}).
          </Typography>
        </Box>
      </Box>

      {/* Portfolio strip */}
      <Box
        sx={{
          display: 'flex',
          border: `1px solid ${colors.borderLight}`,
          borderRadius: 1.5,
          mb: 2.5,
          overflow: 'hidden',
          bgcolor: colors.surfaceSubtle,
        }}
      >
        <PortfolioTile
          eyebrow="Net account change"
          primary={`${netCount >= 0 ? '+' : ''}${netCount}`}
          primaryColor={netCount >= 0 ? colors.success : colors.error}
          secondary={
            <>
              +{newCount} new · −{lostCount} lost
            </>
          }
          tooltip={
            <TooltipBlock
              title="Net account change"
              items={[
                {
                  label: 'Definition',
                  description:
                    'New accounts opened in the recent window minus accounts that went silent.',
                },
                {
                  label: 'This window',
                  description: `${newCount} new · ${lostCount} lost · net ${netCount >= 0 ? '+' : ''}${netCount}`,
                },
              ]}
              tip="Negative means the active customer base is shrinking."
            />
          }
        />
        <PortfolioTile
          eyebrow="Volume swing"
          primary={`${netVol >= 0 ? '+' : ''}${fmt6PkNumber(netVol)} 6-pk`}
          primaryColor={netVol >= 0 ? colors.success : colors.error}
          secondary={
            <>
              {netVol >= 0 ? '+' : ''}
              {fmt6PkNumber(netVol)} 6-pk · +{fmt6PkNumber(newVol)} 6-pk gained · −
              {fmt6PkNumber(lostVol)} 6-pk lost
            </>
          }
          tooltip={
            <TooltipBlock
              title="Volume swing"
              items={[
                {
                  label: 'Definition',
                  description:
                    'Recent-window 6-pk from new accounts minus the prior-window 6-pk that lost accounts used to move.',
                },
                {
                  label: 'This window',
                  description: `+${fmt6PkNumber(newVol)} 6-pk gained · −${fmt6PkNumber(lostVol)} 6-pk lost · net ${netVol >= 0 ? '+' : ''}${fmt6PkNumber(netVol)} 6-pk`,
                },
              ]}
              tip="Positive means the volume gained covers what we lost."
            />
          }
        />
        <PortfolioTile
          eyebrow="Replacement ratio"
          primary={
            replacementRatio === Infinity
              ? '∞'
              : replacementRatio === 0
                ? '—'
                : replacementRatio.toFixed(2) + '×'
          }
          primaryColor={
            replacementRatio >= 1
              ? colors.success
              : replacementRatio >= 0.6
                ? colors.warning
                : colors.error
          }
          secondary={
            <>
              {replacementRatio >= 1
                ? 'Volume more than replaced'
                : replacementRatio >= 0.6
                  ? 'Partial replacement'
                  : 'Losing volume faster than replacing'}
            </>
          }
          tooltip={
            <TooltipBlock
              title="Replacement ratio"
              items={[
                {
                  label: 'Definition',
                  description:
                    'Recent-window 6-pk from new accounts ÷ prior-window 6-pk from lost accounts.',
                },
              ]}
              bullets={[
                '≥ 1.0 — gained at least as much as we lost',
                '0.6 – 0.99 — partial replacement',
                '< 0.6 — losing faster than we replace',
              ]}
              tip="A ratio of 1.0 means we replaced exactly what we lost in pure 6-pk terms."
            />
          }
        />
        <PortfolioTile
          eyebrow="Hotspot — lost"
          primary={hotspotLostState ? hotspotLostState.key : '—'}
          primaryColor={colors.error}
          secondary={
            hotspotLostState ? (
              <>
                {hotspotLostState.count} accounts · −{fmt6PkNumber(hotspotLostState.volume)} 6-pk
              </>
            ) : (
              'No states with lost accounts'
            )
          }
          tooltip={
            <TooltipBlock
              title="Hotspot — lost"
              items={[
                {
                  label: 'Definition',
                  description: 'State with the most lost accounts in this window.',
                },
                hotspotLostState
                  ? {
                      label: hotspotLostState.key,
                      description: `${hotspotLostState.count} lost accounts · ${fmt6PkNumber(hotspotLostState.volume)} 6-pk at risk`,
                    }
                  : {
                      label: '—',
                      description: 'No states have lost accounts in this window.',
                    },
              ]}
              tip="Concentration in one state may signal a territory or distributor issue worth investigating."
            />
          }
        />
      </Box>

      {/* Compact insight row — geographic / channel patterns that
          belong with this analytical section. Per-account call lists
          live in the Follow-Up Tracker so the rep has one hub for
          "what to do today". */}
      <InsightRow
        eyebrow="Patterns"
        chips={[
          ...(topLostState
            ? [
                {
                  key: 'lost-state',
                  Icon: PinDropIcon,
                  color: colors.error,
                  label: 'Lost hotspot',
                  primary: topLostState.key,
                  detail: `${topLostState.count} accounts · −${fmt6PkNumber(topLostState.volume)} 6-pk`,
                },
              ]
            : []),
          ...(topChurnDistributor
            ? [
                {
                  key: 'churn-dist',
                  Icon: WarehouseIcon,
                  color: colors.warning,
                  label: 'Churn dist.',
                  primary: topChurnDistributor.key,
                  detail: `${topChurnDistributor.count} accounts · −${fmt6PkNumber(topChurnDistributor.volume)} 6-pk`,
                },
              ]
            : []),
          ...(hotspotNewState
            ? [
                {
                  key: 'bright-spot',
                  Icon: AutoAwesomeIcon,
                  color: colors.success,
                  label: 'Bright spot',
                  primary: hotspotNewState.key,
                  detail: `${hotspotNewState.count} new · +${fmt6PkNumber(hotspotNewState.volume)} 6-pk`,
                },
              ]
            : []),
        ]}
        followUpHint="Use the Follow-Up Tracker for per-account call lists."
      />

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, city, state, or distributor…"
        />
        <StateMultiSelect
          available={availableStates}
          selected={selectedStates}
          onChange={setSelectedStates}
          open={stateMenuOpen}
          onToggle={() => setStateMenuOpen((o) => !o)}
        />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
          {visibleNew.length} new · {visibleLost.length} lost in view
        </Typography>
      </Box>

      {/* Two-column list */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <AccountPanel
          title="New accounts"
          caption={`Active in ${recentLabel}, silent in the prior window`}
          icon={PersonAddIcon}
          color={colors.success}
          accounts={visibleNew}
          totalCount={newCount}
          totalVolume={newVol}
          isLost={false}
        />
        <AccountPanel
          title="Lost accounts"
          caption={`Active in ${priorLabel}, silent since`}
          icon={PersonOffIcon}
          color={colors.error}
          accounts={visibleLost}
          totalCount={lostCount}
          totalVolume={lostVol}
          isLost={true}
        />
      </Box>

      <SectionExplainer
        rows={[
          {
            sample: <NLSampleNewBadge />,
            term: '"New" accounts',
            desc: (
              <>
                Active in the <strong>recent window</strong> AND silent in the{' '}
                <strong>prior window</strong>. These are first-time buyers in the lookback period —
                the customer base growing.
              </>
            ),
          },
          {
            sample: <NLSampleLostBadge />,
            term: '"Lost" accounts',
            desc: (
              <>
                Active in the <strong>prior window</strong> AND silent in the{' '}
                <strong>recent window</strong>. The exact opposite signal — accounts that stopped
                buying. Pair with Follow-Up Tracker to work the win-back call list.
              </>
            ),
          },
          {
            sample: <NLSamplePortfolioTile />,
            term: 'Portfolio strip (Net change · Volume swing · Replacement · Hotspot)',
            desc: (
              <>
                The four tiles at the top summarize the window in one read: are we gaining or
                shrinking? did the lost volume get replaced? which state is bleeding the most?
              </>
            ),
          },
          {
            sample: <NLSampleInsightRow />,
            term: 'Insight strip',
            desc: (
              <>
                Three quick observations after the totals: lost hotspot state, lost-account
                distributor concentration, and the brightest new-account state. Surfaces patterns
                reps can act on without opening the tables.
              </>
            ),
          },
          {
            sample: (
              <Box
                component="span"
                sx={{
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  px: 0.6,
                  py: 0.15,
                  borderRadius: 0.75,
                  color: colors.info,
                  bgcolor: `${colors.info}14`,
                  border: `1px solid ${colors.info}40`,
                  lineHeight: 1.4,
                }}
              >
                OFF
              </Box>
            ),
            term: 'Premises badge per account',
            desc: (
              <>
                The channel classification beside the name. <strong>ON</strong> bars/restaurants,{' '}
                <strong>OFF</strong> liquor stores/retail, <strong>Online</strong> e-commerce
                retailer (violet), <strong>NA</strong> neither.
              </>
            ),
          },
        ]}
      />
    </Paper>
  );
}

// New vs Lost sample swatches
function NLSampleNewBadge() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: colors.success,
        bgcolor: `${colors.success}14`,
        border: `1px solid ${colors.success}40`,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      New
    </Box>
  );
}
function NLSampleLostBadge() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: colors.error,
        bgcolor: `${colors.error}14`,
        border: `1px solid ${colors.error}40`,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      Lost
    </Box>
  );
}
function NLSamplePortfolioTile() {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        bgcolor: '#fff',
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 1,
        px: 0.6,
        py: 0.3,
        gap: 0.05,
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: 7,
          fontWeight: 800,
          letterSpacing: '0.08em',
          color: colors.textMuted,
          textTransform: 'uppercase',
          lineHeight: 1,
        }}
      >
        Net
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: 10,
          fontWeight: 800,
          color: colors.success,
          lineHeight: 1,
          fontFeatureSettings: '"tnum"',
        }}
      >
        +42
      </Box>
    </Box>
  );
}
function NLSampleInsightRow() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: colors.warning }} />
      <Box component="span" sx={{ fontSize: 9, fontWeight: 700, color: colors.textSecondary }}>
        FL · −18
      </Box>
    </Box>
  );
}

// Compact month/day label for the window headers — e.g. "Mar 1 – Jun 1".
function formatWindow(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const s = new Date(`${start}T00:00:00Z`).toLocaleDateString('en-US', opts);
  const e = new Date(`${end}T00:00:00Z`).toLocaleDateString('en-US', opts);
  return `${s} – ${e}`;
}
